import type { FormatId, TableSettings } from './types.ts';

export interface FormatPreset {
  id: FormatId;
  label: string;
  description: string;
  defaultSeats: number;
  minSeats: number;
  maxSeats: number;
  startingLife: number;
  openingHandSize: number;
  freeFirstMulligan: boolean;
  useCommandZone: boolean;
  useCommanderDamage: boolean;
  teamMode: boolean;
  sharedTeamLife: boolean;
  minDecksForRank: number;
}

export const FORMAT_PRESETS: Record<FormatId, FormatPreset> = {
  commander: {
    id: 'commander',
    label: 'Commander (EDH)',
    description: '100-card singleton, 40 starting life, command zone & 21 commander damage.',
    defaultSeats: 4,
    minSeats: 1,
    maxSeats: 8,
    startingLife: 40,
    openingHandSize: 7,
    freeFirstMulligan: true,
    useCommandZone: true,
    useCommanderDamage: true,
    teamMode: false,
    sharedTeamLife: false,
    minDecksForRank: 3,
  },
  two_headed_giant: {
    id: 'two_headed_giant',
    label: 'Two-Headed Giant',
    description: 'Teams of 2 with shared 30–60 life, shared turns, and up to 4 teams.',
    defaultSeats: 4,
    minSeats: 4,
    maxSeats: 8,
    startingLife: 30,
    openingHandSize: 7,
    freeFirstMulligan: true,
    useCommandZone: true,
    useCommanderDamage: false,
    teamMode: true,
    sharedTeamLife: true,
    minDecksForRank: 2,
  },
  brawl: {
    id: 'brawl',
    label: 'Brawl',
    description: 'Singleton commander format (25 life 1v1, 30 life multiplayer).',
    defaultSeats: 2,
    minSeats: 1,
    maxSeats: 4,
    startingLife: 25,
    openingHandSize: 7,
    freeFirstMulligan: true,
    useCommandZone: true,
    useCommanderDamage: false,
    teamMode: false,
    sharedTeamLife: false,
    minDecksForRank: 2,
  },
  standard: {
    id: 'standard',
    label: 'Standard',
    description: '60-card constructed, 20 starting life, 15-card sideboard.',
    defaultSeats: 2,
    minSeats: 1,
    maxSeats: 8,
    startingLife: 20,
    openingHandSize: 7,
    freeFirstMulligan: false,
    useCommandZone: false,
    useCommanderDamage: false,
    teamMode: false,
    sharedTeamLife: false,
    minDecksForRank: 2,
  },
  modern: {
    id: 'modern',
    label: 'Modern',
    description: '60-card non-rotating constructed, 20 starting life.',
    defaultSeats: 2,
    minSeats: 1,
    maxSeats: 8,
    startingLife: 20,
    openingHandSize: 7,
    freeFirstMulligan: false,
    useCommandZone: false,
    useCommanderDamage: false,
    teamMode: false,
    sharedTeamLife: false,
    minDecksForRank: 2,
  },
  pioneer: {
    id: 'pioneer',
    label: 'Pioneer',
    description: '60-card Return to Ravnica forward constructed, 20 starting life.',
    defaultSeats: 2,
    minSeats: 1,
    maxSeats: 8,
    startingLife: 20,
    openingHandSize: 7,
    freeFirstMulligan: false,
    useCommandZone: false,
    useCommanderDamage: false,
    teamMode: false,
    sharedTeamLife: false,
    minDecksForRank: 2,
  },
  legacy: {
    id: 'legacy',
    label: 'Legacy',
    description: 'Eternal 60-card constructed, 20 starting life.',
    defaultSeats: 2,
    minSeats: 1,
    maxSeats: 8,
    startingLife: 20,
    openingHandSize: 7,
    freeFirstMulligan: false,
    useCommandZone: false,
    useCommanderDamage: false,
    teamMode: false,
    sharedTeamLife: false,
    minDecksForRank: 2,
  },
  vintage: {
    id: 'vintage',
    label: 'Vintage',
    description: 'Eternal restricted-list 60-card constructed, 20 starting life.',
    defaultSeats: 2,
    minSeats: 1,
    maxSeats: 8,
    startingLife: 20,
    openingHandSize: 7,
    freeFirstMulligan: false,
    useCommandZone: false,
    useCommanderDamage: false,
    teamMode: false,
    sharedTeamLife: false,
    minDecksForRank: 2,
  },
  pauper: {
    id: 'pauper',
    label: 'Pauper',
    description: 'Commons-only 60-card constructed, 20 starting life.',
    defaultSeats: 2,
    minSeats: 1,
    maxSeats: 8,
    startingLife: 20,
    openingHandSize: 7,
    freeFirstMulligan: false,
    useCommandZone: false,
    useCommanderDamage: false,
    teamMode: false,
    sharedTeamLife: false,
    minDecksForRank: 2,
  },
  custom: {
    id: 'custom',
    label: 'Custom Sandbox',
    description: 'Fully customizable pod size, starting life, command zone, and team rules.',
    defaultSeats: 2,
    minSeats: 1,
    maxSeats: 8,
    startingLife: 40,
    openingHandSize: 7,
    freeFirstMulligan: true,
    useCommandZone: true,
    useCommanderDamage: true,
    teamMode: false,
    sharedTeamLife: false,
    minDecksForRank: 0,
  },
};

export function createTableSettingsFromPreset(
  format: FormatId,
  overrides: Partial<TableSettings> = {}
): TableSettings {
  const preset = FORMAT_PRESETS[format] ?? FORMAT_PRESETS.commander;
  const isPublic = overrides.isPublic ?? false;
  const maxSeats = Math.max(1, Math.min(8, overrides.maxSeats ?? preset.defaultSeats));
  return {
    name: overrides.name ?? `${preset.label} Table`,
    format,
    maxSeats,
    isPublic,
    isTracked: overrides.isTracked ?? isPublic,
    competitiveMode: overrides.competitiveMode ?? false,
    startingLife: overrides.startingLife ?? preset.startingLife,
    openingHandSize: overrides.openingHandSize ?? preset.openingHandSize,
    freeFirstMulligan: overrides.freeFirstMulligan ?? preset.freeFirstMulligan,
    useCommandZone: overrides.useCommandZone ?? preset.useCommandZone,
    useCommanderDamage: overrides.useCommanderDamage ?? preset.useCommanderDamage,
    teamMode: overrides.teamMode ?? preset.teamMode,
    sharedTeamLife: overrides.sharedTeamLife ?? preset.sharedTeamLife,
    simultaneousTeamTurns: overrides.simultaneousTeamTurns ?? preset.teamMode,
    teammatesSeeHands: overrides.teammatesSeeHands ?? preset.teamMode,
    flagGateMode: overrides.flagGateMode ?? 'phase_and_turn',
    forceStreamMode: overrides.forceStreamMode ?? false,
    forceDefaultCosmetics: overrides.forceDefaultCosmetics ?? false,
    removeCardsOnElimination: overrides.removeCardsOnElimination ?? true,
    spectatorsAllowed: overrides.spectatorsAllowed ?? isPublic,
    allowSpectatorChat: overrides.allowSpectatorChat ?? true,
    allowLateJoinRequests: overrides.allowLateJoinRequests ?? true,
    hasPassword: overrides.hasPassword ?? false,
    bracket: overrides.bracket ?? null,
    language: overrides.language ?? 'English',
    description: overrides.description ?? '',
    spectatorCap: overrides.spectatorCap ?? 50,
    spectatorDelaySec: overrides.spectatorDelaySec ?? (isPublic ? 30 : 0),
    region: overrides.region ?? 'auto',
  };
}
